/**
 * CANCEL-01 — dispatch lifecycle actions (cancel, later reschedule/transfer)
 * recorded as a Pegasus-native OVERLAY on a (read-only) ESC dispatch.
 *
 * Parallel-phase honesty (DEC-22/25): Pegasus can't write ESC yet, so recording
 * a cancellation here logs the intent + reason and reminds the dispatcher to
 * apply it in ESC. At the dispatch cutover the same record drives the native
 * cancel. "Cancellation requested" is DERIVED from this table — never a flag on
 * the ESC row ([[feedback_derive_dont_flag]]). Reversible (void) — never a
 * silent one-click destructive write ([[feedback_review_before_commit]]).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { Modal } from "@/components/ui/Modal";
import type { Dispatch } from "@/lib/types";

/** Plain dispatcher words ([[feedback_plain_labels]]). Admin-editable list = DEC-20 later. */
export const CANCEL_REASONS = [
  "Customer cancelled",
  "Customer rescheduling",
  "Fixed it / no longer needed",
  "Went with another company",
  "Duplicate / made by mistake",
  "Weather",
  "Other",
] as const;

export interface DispatchActionRow {
  id: string;
  action_type: "cancel" | "reschedule" | "transfer";
  esc_dispatch: string | null;
  pegasus_dispatch_id: string | null;
  custno: string | null;
  reason_code: string;
  reason_note: string | null;
  status: "recorded" | "done_in_esc" | "applied" | "voided";
  created_by: string | null;
  created_at: string;
}

/** All live (non-voided) actions keyed by ESC dispatch # — one shared query
 * the board cards read for their overlay (react-query dedupes by key). */
export function useDispatchActionMap() {
  return useQuery({
    queryKey: ["dispatch_actions"],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<Map<string, DispatchActionRow>> => {
      const { data, error } = await supabase
        .from("dispatch_action")
        .select("*")
        .neq("status", "voided")
        .not("esc_dispatch", "is", null);
      if (error) throw error;
      const m = new Map<string, DispatchActionRow>();
      for (const r of (data ?? []) as DispatchActionRow[]) {
        if (r.esc_dispatch) m.set(r.esc_dispatch, r);
      }
      return m;
    },
  });
}

export function useRecordDispatchAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      escDispatch?: string | null;
      pegasusId?: string | null;
      custno?: string | null;
      locno?: string | null;
      actionType?: "cancel" | "reschedule" | "transfer";
      reasonCode: string;
      reasonNote?: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("dispatch_action").insert({
        action_type: v.actionType ?? "cancel",
        esc_dispatch: v.escDispatch ?? null,
        pegasus_dispatch_id: v.pegasusId ?? null,
        custno: v.custno ?? null,
        locno: v.locno ?? null,
        reason_code: v.reasonCode,
        reason_note: v.reasonNote ?? null,
        status: "recorded",
        created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dispatch_actions"] }),
  });
}

export function useVoidDispatchAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("dispatch_action")
        .update({ status: "voided", updated_at: new Date().toISOString(), created_by: u?.user?.email ?? undefined })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dispatch_actions"] }),
  });
}

/**
 * CancelDispatchModal — reason picklist + optional note, then records the
 * cancellation (Pegasus-native) with the parallel-phase reminder. Three close
 * affordances ([[feedback_modal_close_pattern]]).
 */
export function CancelDispatchModal({
  dispatch,
  onClose,
}: {
  dispatch: Dispatch;
  onClose: () => void;
}) {
  const record = useRecordDispatchAction();
  const [reason, setReason] = useState<string>("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Esc / outside-click / focus trap now come from the shared Radix Modal.

  const submit = async () => {
    if (!reason) return;
    setSaving(true);
    try {
      await record.mutateAsync({
        escDispatch: dispatch.dispatch,
        custno: dispatch.custno,
        locno: dispatch.locno ?? null,
        reasonCode: reason,
        reasonNote: note.trim() || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Cancel This Dispatch"
      icon={
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600">
          <Ban className="h-4 w-4" />
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!reason || saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
          >
            <Ban className="h-3.5 w-3.5" />
            {saving ? "Recording…" : "Record cancellation"}
          </button>
        </div>
      }
    >
      <div>
          <p className="text-xs text-slate-500">
            Dispatch #{dispatch.dispatch}
            {dispatch.city ? ` · ${dispatch.city}` : ""}
          </p>

          <div className="mt-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Reason
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CANCEL_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(r)}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                    reason === r
                      ? "border-brand-600 bg-brand-700 text-white"
                      : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <label
              htmlFor="cancel-note"
              className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500"
            >
              Note <span className="font-normal normal-case text-slate-400">(optional)</span>
            </label>
            <textarea
              id="cancel-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Anything the office should know…"
              className="w-full resize-none rounded-md border border-hairline px-2.5 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
            />
          </div>

          <p className="mt-3 rounded-md bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800">
            This records the cancellation in Pegasus and flags the dispatch. Until
            the dispatch cutover, also cancel it in ESC — Pegasus doesn&apos;t write
            ESC yet.
          </p>
      </div>
    </Modal>
  );
}
